import React from "react";
import { cn } from "@/src/lib/utils";
import { HOME_CARD } from "@/src/shared/components/listing/ListingSlider";

/** Enough rails to fill a phone's screen, so the sections below don't jump up. */
const RAILS = 3;

interface IHomeRailsSkeletonProps {
  /** How many rails it stands in for (one for the Featured Stays rail). */
  rails?: number;
}
/**
 * The most cards a rail shows at once (7 from 1440px). `HOME_CARD` sizes them as the rail
 * does, 2 / 3 / 5 / 6 / 7 to a row by width, and the row clips the rest, so every screen
 * shows as many as the loaded rail will.
 */
const CARDS = 7;

/** A loading placeholder in the page's grey. */
const Bone: React.FC<{ className: string }> = ({ className }) => (
  <div className={cn("animate-pulse rounded-md bg-[#EBEBEB]", className)} />
);

/**
 * The home page's rails while a tab loads, shaped like `ListingSlider`'s home row: the title
 * with its round arrow and the two arrows on the right, then `ListingCard`s at the rail's own
 * widths (two to a phone) with the 174px photo, two title lines, the price and the rating.
 */
const HomeRailsSkeleton: React.FC<IHomeRailsSkeletonProps> = ({
  rails = RAILS,
}) => (
  <div aria-hidden>
    {Array.from({ length: rails }, (_, rail) => (
      <div key={rail} className="mb-8">
        <div className="mb-12">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bone className="h-5 w-40 md:h-7 md:w-56" />
              <Bone className="h-7 w-7 rounded-full" />
            </div>
            <div className="flex items-center space-x-2">
              <Bone className="h-8 w-8 rounded-full" />
              <Bone className="h-8 w-8 rounded-full" />
            </div>
          </div>
          <div className="flex gap-6 overflow-hidden lg:gap-3">
            {Array.from({ length: CARDS }, (_, card) => (
              <div key={card} className={cn("flex-shrink-0", HOME_CARD)}>
                <Bone className="h-[174px] w-full rounded-2xl" />
                <div className="mt-2 space-y-2">
                  <Bone className="h-3.5 w-full" />
                  <Bone className="h-3.5 w-3/5" />
                  <div className="flex items-start justify-between">
                    <div className="space-y-1.5">
                      <Bone className="h-4 w-20" />
                      <Bone className="h-4 w-14" />
                    </div>
                    <Bone className="h-3 w-10" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default HomeRailsSkeleton;
